import React, { useState, useEffect, useRef, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const MONITOR_URL = "https://jyswms-endpoint-monitor-k3ehc.ondigitalocean.app";

function playAlarmBeep(ctx) {
  if (!ctx || ctx.state !== 'running') return;
  try {
    const t = ctx.currentTime;
    // Pulse 1: 0.4s on
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, t);
    gain1.gain.setValueAtTime(0.5, t);
    gain1.gain.setValueAtTime(0.5, t + 0.38);
    gain1.gain.linearRampToValueAtTime(0.001, t + 0.4);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(t);
    osc1.stop(t + 0.4);

    // Pulse 2 (0.2s off): 0.4s on
    const t2 = t + 0.6;
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(880, t2);
    gain2.gain.setValueAtTime(0.5, t2);
    gain2.gain.setValueAtTime(0.5, t2 + 0.38);
    gain2.gain.linearRampToValueAtTime(0.001, t2 + 0.4);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(t2);
    osc2.stop(t2 + 0.4);
  } catch {
    // ignore
  }
}

function playTestBeep(ctx) {
  if (!ctx || ctx.state !== 'running') return;
  try {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, t);
    gain.gain.setValueAtTime(0.3, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.15);
  } catch {
    // ignore
  }
}

const SystemHealthPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  const [alarmState, setAlarmState] = useState({
    active: false,
    acknowledged: false,
    reason: '',
    lastSeen: null,
  });
  const [isStale, setIsStale] = useState(false);

  const iframeRef = useRef(null);
  const audioCtxRef = useRef(null);
  const alarmIntervalRef = useRef(null);

  // Helper to ensure AudioContext exists and attempt resume
  const ensureAudioContext = useCallback(() => {
    try {
      if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          audioCtxRef.current = new AudioCtx();
        }
      }
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().then(() => {
          if (audioCtxRef.current?.state === 'running') {
            setIsAudioUnlocked(true);
          }
        }).catch(() => {});
      } else if (audioCtxRef.current?.state === 'running') {
        setIsAudioUnlocked(true);
      }
    } catch {
      // ignore
    }
  }, []);

  // Audio unlock: Try on mount and register once-only user interaction listeners
  useEffect(() => {
    ensureAudioContext();

    const handleInteraction = () => {
      ensureAudioContext();
    };

    const events = ['click', 'keydown', 'touchstart'];
    events.forEach((evt) => {
      window.addEventListener(evt, handleInteraction, { once: true, passive: true });
    });

    return () => {
      events.forEach((evt) => {
        window.removeEventListener(evt, handleInteraction);
      });
    };
  }, [ensureAudioContext]);

  // Click handler for the Sound status button (unlocks audio and plays test beep)
  const handleSoundButtonClick = async () => {
    try {
      if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          audioCtxRef.current = new AudioCtx();
        }
      }
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        await audioCtxRef.current.resume();
      }
      if (audioCtxRef.current?.state === 'running') {
        setIsAudioUnlocked(true);
        playTestBeep(audioCtxRef.current);
      }
    } catch {
      // ignore
    }
  };

  // Message listener for events from the embedded monitor
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== MONITOR_URL) return;
      if (event.data?.type !== 'jy-monitor-alarm') return;

      const { active, acknowledged, reason } = event.data;
      setAlarmState({
        active: Boolean(active),
        acknowledged: Boolean(acknowledged),
        reason: reason || 'System unhealthy',
        lastSeen: Date.now(),
      });
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Staleness checker: if no message arrives for 15s while alarm is active
  useEffect(() => {
    const timer = setInterval(() => {
      if (alarmState.active && alarmState.lastSeen) {
        const elapsed = Date.now() - alarmState.lastSeen;
        setIsStale(elapsed > 15000);
      } else {
        setIsStale(false);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [alarmState.active, alarmState.lastSeen]);

  // Alarm sound loop: repeats every 2 seconds while active and not acknowledged
  useEffect(() => {
    const shouldRing = alarmState.active && !alarmState.acknowledged;

    if (!shouldRing) {
      if (alarmIntervalRef.current) {
        clearInterval(alarmIntervalRef.current);
        alarmIntervalRef.current = null;
      }
      return;
    }

    const ring = () => {
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state !== 'closed') {
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }
        if (ctx.state === 'running') {
          playAlarmBeep(ctx);
        }
      }
    };

    ring();
    alarmIntervalRef.current = setInterval(ring, 2000);

    return () => {
      if (alarmIntervalRef.current) {
        clearInterval(alarmIntervalRef.current);
        alarmIntervalRef.current = null;
      }
    };
  }, [alarmState.active, alarmState.acknowledged]);

  // Clean up AudioContext when unmounting page
  useEffect(() => {
    return () => {
      if (alarmIntervalRef.current) {
        clearInterval(alarmIntervalRef.current);
        alarmIntervalRef.current = null;
      }
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
    };
  }, []);

  // Acknowledge alarm
  const handleAcknowledge = () => {
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'jy-monitor-ack' },
        MONITOR_URL
      );
    } catch {
      // ignore
    }
    setAlarmState((prev) => ({ ...prev, acknowledged: true }));
  };

  // Fallback timer for iframe loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading((loading) => {
        if (loading) {
          setHasError(true);
          return false;
        }
        return loading;
      });
    }, 12000);

    return () => clearTimeout(timer);
  }, []);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const isAlarmRinging = alarmState.active && !alarmState.acknowledged;

  return (
    <DashboardLayout disablePadding>
      <Box
        sx={{
          width: '100%',
          height: { xs: 'calc(100vh - 40px)', sm: 'calc(100vh - 48px)' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: '#0b1220',
          m: 0,
          p: 0,
          '@keyframes slowPulse': {
            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.88, transform: 'scale(0.985)' },
          },
        }}
      >
        {/* Toolbar: First child (height 48px, flex: none) */}
        <Box
          component="header"
          sx={{
            height: '48px',
            minHeight: '48px',
            maxHeight: '48px',
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            background: '#0b1220',
            borderBottom: '1px solid #1f2a3d',
            position: 'relative',
            zIndex: 1,
            boxSizing: 'border-box',
            gap: '12px',
          }}
        >
          {/* Left side: Sound control pill button */}
          <Button
            onClick={handleSoundButtonClick}
            sx={{
              height: '32px',
              minHeight: '32px',
              padding: '0 14px',
              fontSize: '13px',
              fontWeight: 600,
              borderRadius: '999px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              textTransform: 'none',
              boxShadow: 'none',
              cursor: 'pointer',
              flexShrink: 0,
              backgroundColor: isAudioUnlocked
                ? 'rgba(34, 197, 94, 0.15)'
                : 'rgba(245, 158, 11, 0.15)',
              border: isAudioUnlocked
                ? '1px solid #22c55e'
                : '1px solid #f59e0b',
              color: isAudioUnlocked ? '#4ade80' : '#fbbf24',
              '&:hover': {
                backgroundColor: isAudioUnlocked
                  ? 'rgba(34, 197, 94, 0.25)'
                  : 'rgba(245, 158, 11, 0.25)',
                boxShadow: 'none',
              },
            }}
          >
            {isAudioUnlocked ? (
              <VolumeUpIcon sx={{ fontSize: '16px' }} />
            ) : (
              <VolumeOffIcon sx={{ fontSize: '16px' }} />
            )}
            {isAudioUnlocked && (
              <Box
                component="span"
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#22c55e',
                  display: 'inline-block',
                }}
              />
            )}
            <span>
              {isAudioUnlocked ? 'Sound on' : 'Click to enable alarm sound'}
            </span>
          </Button>

          {/* Middle: Alarm status pill and Acknowledge button when ringing */}
          {isAlarmRinging && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minWidth: 0,
                flexShrink: 1,
              }}
            >
              <Box
                sx={{
                  height: '32px',
                  minHeight: '32px',
                  padding: '0 14px',
                  borderRadius: '999px',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  animation: 'slowPulse 2s ease-in-out infinite',
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                  boxSizing: 'border-box',
                  whiteSpace: 'nowrap',
                }}
              >
                <WarningAmberIcon sx={{ fontSize: '16px', flexShrink: 0 }} />
                <span>ALARM</span>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#ffffff',
                    maxWidth: '300px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '@media (max-width: 699px)': {
                      display: 'none',
                    },
                  }}
                >
                  {`: ${alarmState.reason || 'System unhealthy'}`}
                  {isStale && ' (No update from monitor)'}
                </Typography>
              </Box>

              <Button
                onClick={handleAcknowledge}
                sx={{
                  height: '32px',
                  minHeight: '32px',
                  padding: '0 14px',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  color: '#991b1b',
                  fontSize: '13px',
                  fontWeight: 700,
                  textTransform: 'none',
                  border: '1px solid #ffffff',
                  boxShadow: 'none',
                  cursor: 'pointer',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: '#f3f4f6',
                    color: '#7f1d1d',
                    boxShadow: 'none',
                  },
                }}
              >
                Acknowledge
              </Button>
            </Box>
          )}

          {/* Right side: Open in new tab link */}
          <Button
            component="a"
            href={MONITOR_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              height: '32px',
              minHeight: '32px',
              padding: '0 14px',
              fontSize: '13px',
              fontWeight: 500,
              borderRadius: '8px',
              backgroundColor: '#1f2a3d',
              border: '1px solid #374151',
              color: '#e5e7eb',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              textTransform: 'none',
              boxShadow: 'none',
              cursor: 'pointer',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: '#2a3850',
                color: '#ffffff',
                borderColor: '#4b5563',
                boxShadow: 'none',
              },
              '&:focus-visible': {
                outline: '2px solid #60a5fa',
                outlineOffset: '2px',
              },
            }}
          >
            <span>Open in new tab</span>
            <OpenInNewIcon sx={{ fontSize: '16px' }} />
          </Button>
        </Box>

        {/* Content area: Iframe is the second child (flex: 1, width: 100%, border: 0, min-height: 0) */}
        <Box
          sx={{
            flex: 1,
            width: '100%',
            minHeight: 0,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#111827',
          }}
        >
          {/* Loading state indicator */}
          {isLoading && !hasError && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#111827',
                zIndex: 5,
              }}
            >
              <CircularProgress size={36} sx={{ color: '#60a5fa', mb: 1.5 }} />
              <Typography
                variant="body2"
                sx={{ color: '#9ca3af', fontWeight: 500, fontSize: '0.875rem' }}
              >
                Loading Live System Health...
              </Typography>
            </Box>
          )}

          {/* Fallback error view if iframe is blocked */}
          {hasError ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#111827',
                p: 3,
                textAlign: 'center',
                zIndex: 20,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#f3f4f6',
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  mb: 1,
                }}
              >
                Could not embed the monitor here.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#9ca3af',
                  maxWidth: 420,
                  mb: 2.5,
                  fontSize: '0.875rem',
                }}
              >
                The external monitoring dashboard could not be loaded inside this frame.
                You can open it directly in a new tab.
              </Typography>
              <Button
                component="a"
                href={MONITOR_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<OpenInNewIcon />}
                sx={{
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  borderColor: '#3b82f6',
                  px: 2.5,
                  py: 1,
                  height: '38px',
                  '&:hover': {
                    backgroundColor: '#1d4ed8',
                  },
                }}
              >
                Open in new tab
              </Button>
            </Box>
          ) : (
            <iframe
              ref={iframeRef}
              src={MONITOR_URL}
              title="Live System Health"
              allow="autoplay"
              onLoad={handleLoad}
              onError={handleError}
              style={{
                width: '100%',
                height: '100%',
                border: 0,
                margin: 0,
                padding: 0,
                display: 'block',
              }}
            />
          )}
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default SystemHealthPage;
